import {
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Popper,
} from '@mui/material';
import React from 'react';
import { CustomColorPalette } from '../types';
import { SearchResultCard } from './SearchResultCard';
import { SearchResult } from './useSearch';

interface SearchDropdownProps {
  anchorEl: HTMLElement | null;
  searchValue: string;
  searchResults: SearchResult[];
  isSearchLoading: boolean;
  isDarkMode: boolean;
  palette: CustomColorPalette;
  onResultClick: (result: SearchResult) => void;
  resultsRef: React.RefObject<HTMLDivElement>;
}

export const SearchDropdown: React.FC<SearchDropdownProps> = ({
  anchorEl,
  searchValue,
  searchResults,
  isSearchLoading,
  isDarkMode,
  palette,
  onResultClick,
  resultsRef,
}) => {
  if (!anchorEl) return null;

  return (
    <Popper
      open={Boolean(
        anchorEl &&
          searchValue.trim() &&
          (searchResults.length > 0 || isSearchLoading),
      )}
      anchorEl={anchorEl}
      placement='bottom-start'
      style={{ width: anchorEl?.clientWidth, zIndex: 1300 }}
    >
      <Paper
        ref={resultsRef}
        sx={{
          width: '100%',
          overflowY: 'auto',
          maxHeight: '350px',
          backgroundColor: isDarkMode ? palette.gray[900] : palette.gray.A100,
          border: `1px solid ${
            isDarkMode ? palette.gray[700] : palette.gray[300]
          }`,
          borderRadius: '8px',
          marginTop: '4px',
        }}
      >
        {isSearchLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '16px',
            }}
          >
            <CircularProgress size={24} />
          </Box>
        ) : (
          <List sx={{ padding: '8px' }}>
            {searchResults.length > 0 ? (
              searchResults.map((result, index) => (
                <SearchResultCard
                  key={result.id}
                  id={result.id}
                  name={result.name}
                  type={result.type}
                  spaceName={result.spaceName}
                  isDarkMode={isDarkMode}
                  palette={palette}
                  onClick={() => onResultClick(result)}
                  isLast={index === searchResults.length - 1}
                />
              ))
            ) : searchValue.trim() ? (
              <ListItem>
                <ListItemText
                  primary='No results'
                  primaryTypographyProps={{
                    sx: {
                      color: isDarkMode ? palette.gray[500] : palette.gray[600],
                      fontFamily: 'Roboto',
                      fontSize: '14px',
                    },
                  }}
                />
              </ListItem>
            ) : null}
          </List>
        )}
      </Paper>
    </Popper>
  );
};
